import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import {
  Mosaic,
  MosaicWindow,
  MosaicNode,
  MosaicBranch,
} from 'react-mosaic-component'
import 'react-mosaic-component/react-mosaic-component.css'
import { Terminal, Bot, Globe, FolderOpen, Eye, BookOpen, GitBranch, Key } from 'lucide-react'
import XpraPanel from './XpraPanel'
import DocsPanel from './DocsPanel'
import GitPanel from './GitPanel'
import SecretsPanel from './SecretsPanel'
import TerminalTabs from './TerminalTabs'
import FileBrowserPanel from './FileBrowserPanel'
import LocalBrowserPanel from './LocalBrowserPanel'
import ClaudePanel from './ClaudePanel'
import type { PanelType } from './PanelToggle'
import { useSettings } from '../../hooks/useSettings'
import { withMountBase } from '../../lib/mountBase'

// Panel identifiers
type PanelId = 'main' | 'browser' | 'files' | 'preview' | 'docs' | 'git' | 'secrets'

interface WorkspaceLayoutProps {
  activePanels: Set<PanelType>
  onPanelClose: (panel: PanelType) => void
  onRequestSession?: (sessionName: string) => void
  cwd?: string  // Initial working directory for terminal
  browserUrl?: string  // URL for browser panel to open
  appName?: string  // App name for per-app workspace container routing
  tmuxSession?: string  // Specific tmux session to attach to
  activeSession?: string  // Current active session, owned by the parent (single source of truth)
  requestedSession?: string  // Session requested by external dropdown (triggers tab switch in TerminalTabs)
  onSessionChange?: (sessionName: string) => void  // Notify parent when active session changes
}

// Panel titles and icons — main panel label/icon depends on claude_panel_enabled setting
function getPanelMeta(claudePanelEnabled: boolean): Record<PanelId, { title: string; icon: React.ReactNode }> {
  return {
    main: claudePanelEnabled
      ? { title: 'Claude', icon: <Bot className="w-4 h-4" /> }
      : { title: 'Terminal', icon: <Terminal className="w-4 h-4" /> },
    browser: { title: 'Server Browser', icon: <Globe className="w-4 h-4" /> },
    files: { title: 'Files', icon: <FolderOpen className="w-4 h-4" /> },
    preview: { title: 'Preview', icon: <Eye className="w-4 h-4" /> },
    docs: { title: 'Docs', icon: <BookOpen className="w-4 h-4" /> },
    git: { title: 'Source & Release', icon: <GitBranch className="w-4 h-4" /> },
    secrets: { title: 'App secrets', icon: <Key className="w-4 h-4" /> },
  }
}

// Storage key for layout
const LAYOUT_STORAGE_KEY = 'workspace-layout'

export default function WorkspaceLayout({
  activePanels,
  onPanelClose,
  onRequestSession,
  cwd,
  browserUrl,
  appName,
  tmuxSession,
  activeSession: activeSessionProp,
  requestedSession,
  onSessionChange: onSessionChangeProp,
}: WorkspaceLayoutProps) {
  const { settings } = useSettings()
  const claudePanelEnabled = !!(settings?.dashboard as Record<string, unknown>)?.claude_panel_enabled

  // Store layout before maximizing so we can restore it
  const savedLayoutRef = useRef<MosaicNode<PanelId> | null>(null)
  const [isMaximized, setIsMaximized] = useState(false)

  // Track which panel is currently focused to prevent focus jumping
  const [activePanel, setActivePanel] = useState<PanelId>('main')

  // The active session is owned by the parent (Workspace.tsx) — the same value
  // that drives the preview URL — and passed down here. Deriving it straight
  // from the prop (rather than a local copy synced off requestedSession) means
  // the ClaudePanel session can't drift out of step with the preview: every
  // switch, including re-selecting the same workarea, flows through here.
  const activeSession = activeSessionProp ?? tmuxSession ?? 'main'

  // Session changes from inside the layout (TerminalTabs tab switch, GitPanel
  // "open session") notify the parent, which updates activeSession and feeds it
  // back down as the prop. No local copy to fall out of sync.
  const setActiveSession = useCallback((session: string) => {
    onSessionChangeProp?.(session)
  }, [onSessionChangeProp])

  // Derive effective cwd based on session (worktrees are in different paths)
  const effectiveCwd = useMemo(() => {
    if (appName && activeSession && activeSession !== 'main') {
      return `/home/coder/workspace/worktrees/${activeSession}`
    }
    return cwd
  }, [appName, activeSession, cwd])

  // Build initial layout based on active panels
  const buildInitialLayout = useCallback((): MosaicNode<PanelId> | null => {
    const panels: PanelId[] = ['main']
    if (activePanels.has('preview')) panels.push('preview')
    if (activePanels.has('browser')) panels.push('browser')
    if (activePanels.has('files')) panels.push('files')
    if (activePanels.has('docs')) panels.push('docs')
    if (activePanels.has('git')) panels.push('git')
    if (activePanels.has('secrets')) panels.push('secrets')

    if (panels.length === 1) {
      return 'main'
    }

    if (panels.length === 2) {
      return {
        direction: 'row',
        first: 'main',
        second: panels[1],
        splitPercentage: 50,
      }
    }

    // 3+ panels: main on left, other panels stacked on right
    const sidePanels = panels.slice(1)

    if (sidePanels.length === 5) {
      return {
        direction: 'row',
        first: 'main',
        second: {
          direction: 'column',
          first: {
            direction: 'row',
            first: sidePanels[0],
            second: sidePanels[1],
            splitPercentage: 50,
          },
          second: {
            direction: 'column',
            first: sidePanels[2],
            second: {
              direction: 'row',
              first: sidePanels[3],
              second: sidePanels[4],
              splitPercentage: 50,
            },
            splitPercentage: 50,
          },
          splitPercentage: 40,
        },
        splitPercentage: 48,
      }
    }

    if (sidePanels.length === 4) {
      return {
        direction: 'row',
        first: 'main',
        second: {
          direction: 'column',
          first: {
            direction: 'row',
            first: sidePanels[0],
            second: sidePanels[1],
            splitPercentage: 50,
          },
          second: {
            direction: 'row',
            first: sidePanels[2],
            second: sidePanels[3],
            splitPercentage: 50,
          },
          splitPercentage: 50,
        },
        splitPercentage: 50,
      }
    }

    if (sidePanels.length === 2) {
      return {
        direction: 'row',
        first: 'main',
        second: {
          direction: 'column',
          first: sidePanels[0],
          second: sidePanels[1],
          splitPercentage: 50,
        },
        splitPercentage: 50,
      }
    }

    // 4 panels: main on left, 3 panels stacked on right
    return {
      direction: 'row',
      first: 'main',
      second: {
        direction: 'column',
        first: sidePanels[0],
        second: {
          direction: 'column',
          first: sidePanels[1],
          second: sidePanels[2],
          splitPercentage: 50,
        },
        splitPercentage: 33,
      },
      splitPercentage: 50,
    }
  }, [activePanels])

  const [layout, setLayout] = useState<MosaicNode<PanelId> | null>(buildInitialLayout)

  // Update layout when panels change
  useEffect(() => {
    setLayout(buildInitialLayout())
    setIsMaximized(false)
    savedLayoutRef.current = null
  }, [buildInitialLayout])

  // Toggle maximize - uses mosaic's controlled layout
  const handleMaximize = useCallback((id: PanelId) => {
    if (isMaximized) {
      // Restore previous layout
      if (savedLayoutRef.current) {
        setLayout(savedLayoutRef.current)
      }
      savedLayoutRef.current = null
      setIsMaximized(false)
    } else {
      // Save current layout and set to single panel
      savedLayoutRef.current = layout
      setLayout(id)
      setIsMaximized(true)
    }
  }, [isMaximized, layout])

  const panelMeta = useMemo(() => getPanelMeta(claudePanelEnabled), [claudePanelEnabled])

  // Render panel content
  const renderPanel = useCallback(
    (id: PanelId, path: MosaicBranch[]) => {
      const meta = panelMeta[id]
      const isCloseable = id !== 'main'
      const panelIsMaximized = isMaximized && layout === id

      const handleClose = () => {
        if (id === 'browser') onPanelClose('browser')
        if (id === 'files') onPanelClose('files')
        if (id === 'preview') onPanelClose('preview')
        if (id === 'docs') onPanelClose('docs')
        if (id === 'git') onPanelClose('git')
        if (id === 'secrets') onPanelClose('secrets')
      }

      // Render the appropriate panel content
      let content: React.ReactNode

      if (id === 'main') {
        if (claudePanelEnabled) {
          // Claude panel — structured WebSocket communication with Claude Code CLI
          content = (
            <ClaudePanel
              appName={appName || ''}
              session={activeSession}
              closeable={false}
              isMaximized={panelIsMaximized}
              onMaximize={() => handleMaximize(id)}
            />
          )
        } else {
          // TerminalTabs manages multiple terminal sessions with tab bar UI
          content = (
            <TerminalTabs
              appName={appName || ''}
              initialSession={tmuxSession}
              onSessionChange={setActiveSession}
              requestedSession={requestedSession}
            />
          )
        }
      } else if (id === 'browser') {
        // Browser (Chrome) uses XpraPanel for better connection handling
        // Use per-app browser route if appName is available
        const browserBaseUrl = withMountBase(
          appName
            ? `/workspace/app/${encodeURIComponent(appName)}/browser/`
            : '/browser/'
        )
        content = (
          <XpraPanel
            title="Browser"
            icon={<Globe className="w-4 h-4" />}
            baseUrl={browserBaseUrl}
            startUrl={browserUrl}
            onClose={handleClose}
            closeable={isCloseable}
            isMaximized={panelIsMaximized}
            onMaximize={() => handleMaximize(id)}
          />
        )
      } else if (id === 'files') {
        content = (
          <FileBrowserPanel
            key={`files-${activeSession}`}
            onClose={handleClose}
            closeable={isCloseable}
            initialPath={effectiveCwd}
            appName={appName}
          />
        )
      } else if (id === 'preview') {
        content = (
          <LocalBrowserPanel
            initialUrl={browserUrl}
            onClose={handleClose}
            closeable={isCloseable}
            isMaximized={panelIsMaximized}
            onMaximize={() => handleMaximize(id)}
          />
        )
      } else if (id === 'docs') {
        content = (
          <DocsPanel
            key={`docs-${activeSession}`}
            appName={appName || ''}
            rootPath={effectiveCwd}
            onClose={handleClose}
            closeable={isCloseable}
            isMaximized={panelIsMaximized}
            onMaximize={() => handleMaximize(id)}
          />
        )
      } else if (id === 'git') {
        content = appName ? (
          <GitPanel
            appName={appName}
            appFolder={cwd}
            onClose={handleClose}
            closeable={isCloseable}
            isMaximized={panelIsMaximized}
            onMaximize={() => handleMaximize(id)}
            onOpenSession={(terminalUrl) => {
              const session = new URLSearchParams(terminalUrl.split('?')[1] || '').get('arg')
              if (session) {
                onRequestSession?.(session)
                setActiveSession(session)
              }
            }}
          />
        ) : null
      } else if (id === 'secrets') {
        content = appName ? (
          <SecretsPanel
            appName={appName}
            onClose={handleClose}
            closeable={isCloseable}
            isMaximized={panelIsMaximized}
            onMaximize={() => handleMaximize(id)}
          />
        ) : null
      }

      // Handle panel click: activate panel and focus iframe if present
      const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
        setActivePanel(id)
        // For panels with iframes (browser, preview), focus the iframe after activation
        // Use setTimeout to let React re-render with pointer-events: auto
        if (id === 'browser' || id === 'preview') {
          setTimeout(() => {
            const iframe = e.currentTarget.querySelector('iframe')
            if (iframe) {
              iframe.focus()
            }
          }, 0)
        }
      }

      return (
        <MosaicWindow<PanelId>
          path={path}
          title={meta?.title || 'Panel'}
          toolbarControls={<></>}
          renderToolbar={() => <div style={{ display: 'none' }} />}
          createNode={() => 'main'}
        >
          <div
            className={`h-full panel-container ${activePanel === id ? 'panel-active' : 'panel-inactive'}`}
            onClick={handlePanelClick}
            onFocus={() => setActivePanel(id)}
          >
            {content}
          </div>
        </MosaicWindow>
      )
    },
    [onPanelClose, isMaximized, layout, handleMaximize, effectiveCwd, browserUrl, appName, activeSession, activePanel, setActivePanel, claudePanelEnabled, panelMeta, tmuxSession, requestedSession, setActiveSession, cwd, onRequestSession]
  )

  // Handle layout change
  const handleLayoutChange = useCallback((newLayout: MosaicNode<PanelId> | null) => {
    setLayout(newLayout)
    // Save to localStorage
    if (newLayout) {
      try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(newLayout))
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [])

  return (
    <div className="h-full workspace-mosaic relative">
      <style>{`
        .workspace-mosaic .mosaic {
          background: hsl(var(--background));
        }
        .workspace-mosaic .mosaic-root {
          background: hsl(var(--background));
        }
        .workspace-mosaic .mosaic-tile {
          margin: 2px;
        }
        .workspace-mosaic .mosaic-window {
          border-radius: 8px;
          overflow: hidden;
        }
        .workspace-mosaic .mosaic-window-toolbar {
          display: none;
        }
        .workspace-mosaic .mosaic-window-body {
          background: transparent;
        }
        .workspace-mosaic .mosaic-split {
          background: hsl(var(--background));
        }
        .workspace-mosaic .mosaic-split:hover {
          background: hsl(var(--primary) / 0.3);
        }
        .workspace-mosaic .mosaic-split.-row {
          width: 6px;
          margin: 0 -3px;
        }
        .workspace-mosaic .mosaic-split.-column {
          height: 6px;
          margin: -3px 0;
        }
        /* Focus management - prevent inactive panels from stealing focus */
        .panel-container {
          position: relative;
        }
        .panel-inactive iframe {
          pointer-events: none;
        }
        .panel-active iframe {
          pointer-events: auto;
        }
        /* Visual indicator for active panel */
        .panel-active {
          outline: 2px solid hsl(var(--primary) / 0.3);
          outline-offset: -2px;
        }
      `}</style>
      <div className="h-full">
        <Mosaic<PanelId>
          renderTile={renderPanel}
          value={layout}
          onChange={handleLayoutChange}
          className="mosaic-blueprint-theme"
        />
      </div>
    </div>
  )
}
